import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CacheManagement = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [cacheStatus, setCacheStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    if (!token) {
      navigate('/login');
      return;
    }
    testCache();
  }, [user, token, navigate]);

  const testCache = async () => {
    try {
      const response = await axios.get(`${API}/cache/test`);
      setCacheStatus(response.data);
    } catch (error) {
      console.error('Error testing cache:', error);
      setCacheStatus({ status: 'error', message: 'Failed to connect to cache' });
    } finally {
      setLoading(false);
    }
  };

  const clearCache = async (pattern) => {
    setClearing(true);
    setMessage('');
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/cache/clear`, { pattern }, config);
      setMessage(`✅ ${response.data.message}`);
      testCache();
    } catch (error) {
      setMessage(`❌ Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setClearing(false);
    }
  };

  const clearProductCache = async () => {
    setClearing(true);
    setMessage('');
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/cache/clear/products`, {}, config);
      setMessage(`✅ ${response.data.message}`);
      testCache();
    } catch (error) {
      setMessage(`❌ Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setClearing(false);
    }
  };

  const clearAdminCache = async () => {
    setClearing(true);
    setMessage('');
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/cache/clear/admin`, {}, config);
      setMessage(`✅ ${response.data.message}`);
      testCache();
    } catch (error) {
      setMessage(`❌ Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-4 md:px-6 lg:px-8 md:py-8">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 md:mb-8 gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Cache</h1>
            <p className="mt-1 md:mt-2 text-sm text-gray-600">Monitor and manage Redis cache</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="px-3 md:px-4 py-2 border border-gray-300 rounded-md text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-50 self-start"
          >
            Back
          </button>
        </div>

        {/* Cache Status */}
        <div className="bg-white shadow rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h2 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Cache Status</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm font-medium text-gray-700">Status:</span>
              <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold ${
                cacheStatus?.status === 'connected' ? 'bg-green-100 text-green-800' :
                cacheStatus?.status === 'disconnected' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {cacheStatus?.status?.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm font-medium text-gray-700">Message:</span>
              <span className="text-xs md:text-sm text-gray-600 text-right">{cacheStatus?.message}</span>
            </div>
            {cacheStatus?.timestamp && (
              <div className="flex items-center justify-between">
                <span className="text-xs md:text-sm font-medium text-gray-700">Last Checked:</span>
                <span className="text-xs md:text-sm text-gray-600">{new Date(cacheStatus.timestamp).toLocaleString()}</span>
              </div>
            )}
          </div>
          <button
            onClick={testCache}
            className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded-md text-xs md:text-sm font-medium hover:bg-indigo-700"
          >
            Refresh Status
          </button>
        </div>

        {/* Cache Actions */}
        <div className="bg-white shadow rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h2 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Cache Actions</h2>
          
          {message && (
            <div className={`mb-4 p-3 md:p-4 rounded-md text-sm ${
              message.startsWith('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={clearProductCache}
              disabled={clearing}
              className="w-full px-3 md:px-4 py-2 md:py-3 bg-blue-600 text-white rounded-md text-xs md:text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clearing ? 'Clearing...' : 'Clear Product Cache'}
            </button>

            <button
              onClick={clearAdminCache}
              disabled={clearing}
              className="w-full px-3 md:px-4 py-2 md:py-3 bg-purple-600 text-white rounded-md text-xs md:text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clearing ? 'Clearing...' : 'Clear Admin Cache'}
            </button>

            <button
              onClick={() => clearCache('*')}
              disabled={clearing}
              className="w-full px-3 md:px-4 py-2 md:py-3 bg-red-600 text-white rounded-md text-xs md:text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clearing ? 'Clearing...' : 'Clear All Cache'}
            </button>
          </div>
        </div>

        {/* Cache Info */}
        <div className="bg-white shadow rounded-lg p-4 md:p-6">
          <h2 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Cache Information</h2>
          <div className="space-y-3 text-xs md:text-sm text-gray-600">
            <div>
              <span className="font-medium text-gray-900">Product Cache:</span>
              <p className="mt-1">Products are cached for 5 minutes. Automatically cleared when products are created, updated, or deleted.</p>
            </div>
            <div>
              <span className="font-medium text-gray-900">Admin Cache:</span>
              <p className="mt-1">Admin stats and data are cached for 2-10 minutes depending on the endpoint.</p>
            </div>
            <div>
              <span className="font-medium text-gray-900">Cache Strategy:</span>
              <p className="mt-1">The application uses Redis for caching with automatic invalidation on data mutations. If Redis is unavailable, the app continues to work without caching.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CacheManagement;
